import React, { useContext, useEffect, useState } from "react";
import { Button, Grid } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { FormProvider, useForm } from "react-hook-form";
import DnerhsApi from "api/DnerhsApi";
import { Link, useParams } from "react-router-dom";
import UserContext from "context/User/UserContext";
import FormDatePicker from "Controls/DatePicker";
import Alert from "components/Alert";
import history from "utils/History";
import { trackPromise } from 'react-promise-tracker';
import FormInput from "../../../Controls/Input";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers";
import FormCheckBox from "Controls/Checkbox";

const useStyles = makeStyles((theme) => ({
  main: {
    paddingLeft: theme.spacing(10),
    paddingRight: theme.spacing(10),
  },
  form: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  field: {
    margin: theme.spacing(),
  },
}));

const validationSchema = yup.object().shape({
  fechaFirma: yup.string().required("El campo fecha de firma es requerido"),
  fechaInicio: yup.string().required("El campo fecha de inicio es requerido"),
  fechaFin: yup.string().required("El campo fecha fin es requerido"),
  resolucion: yup.string().required("El campo resolucion es requerido")
});

const Form = () => {
  const classes = useStyles();
  const { userData } = useContext(UserContext);
  const [payload, setPayload] = useState();
  const isAdmin = userData && userData.role.descripcion === "ROLE_ADMIN";
  const params = useParams();
  const methods = useForm({
    resolver: yupResolver(validationSchema)
  });
  const { handleSubmit, errors, reset, getValues } = methods;
  const [sent, setSent] = useState(false);
  const [presentaCarta, setPresentaCarta] = useState(false);
  const [renovacionConvenio, setRenovacionConvenio] = useState(false); // Nuevo estado
  const [institucionFormadoraId, setInstitucionFormadoraId] = useState(false);

  useEffect(() => {
    const load = () => {
      try {
        setValuesForEdit();
      } catch (error) {
        console.log(error);
      }
    };

    load();
  }, []);

  const setValuesForEdit = async () => {
    try {
      let url = `/convenios/${params.convenioId}`;
      const response = await DnerhsApi(url);
      setInstitucionFormadoraId(response.data.institucionFormadora.id)
      setPayload(response.data);
    } catch (error) {
      console.log(error);
    }
  };

 const onSubmit = async (data) => {
  try {
    let esPrimerConvenio = false;

    // Verificar si es el primer convenio
    let urlPrimerConvenio = `/convenios/institucionFormadora/${institucionFormadoraId}/primer-convenio`;
    const { data: responsePrimerConvenio } = await DnerhsApi(urlPrimerConvenio);

    if (responsePrimerConvenio?.convenioId.toString() === params.convenioId.toString()) {
      esPrimerConvenio = true;
    }

    if (!esPrimerConvenio && !getValues("presentaCarta")) {
      Alert.show({
        message: "Es requerido presentar el documento de Carta de Compromiso.",
        type: "error",
      });
      return;
    }

    // MEJORA: Formatear fechas correctamente
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const fechaFirmaFormatted = formatDate(data.fechaFirma);
    const fechaInicioFormatted = formatDate(data.fechaInicio);
    const fechaFinFormatted = formatDate(data.fechaFin);

    // Construcción de la URL con fechas formateadas
    let url = `/convenios/${params.convenioId}/aprobacion?estado=Convenio firmado` +
              `&fechaFinVigencia=${fechaFinFormatted}` +
              `&fechaFirma=${fechaFirmaFormatted}` +
              `&fechaInicioVigencia=${fechaInicioFormatted}` +
              `&numeroResolucion=${data.resolucion}` +
              `&cartaCompromiso=${data.presentaCarta}` +
              `&renovacionConvenio=${data.renovacionConvenio}` +
              `&id=${params.convenioId}` +
              `&observacion=`;

    console.log("URL enviada:", url);

    // Llamada sin timeout artificial, el controller ahora responde rápido
    const response = await trackPromise(DnerhsApi.post(url));

    console.log("Respuesta recibida:", response.data);

    // Verificar si la respuesta es exitosa
    if (response.data && response.data.success) {
      setSent(true);
      
      Alert.show({
        message: "Datos guardados exitosamente",
        type: "success"
      });

      // Esperar un momento antes de navegar para que el usuario vea el mensaje
      setTimeout(() => {
        history.goBack();
      }, 1500);
    } else {
      throw new Error(response.data?.message || "Respuesta inesperada del servidor");
    }

  } catch (error) {
    console.error("Error:", error);

    if (error.response) {
      // Error del servidor con respuesta
      if (error.response.data?.message) {
        Alert.show({
          message: error.response.data.message,
          type: "error"
        });
      } else {
        Alert.showServerError(error);
      }
    } else if (error.message?.includes("timeout")) {
      // Error de timeout
      Alert.show({
        message: "La operación está tomando más tiempo del esperado. Verificando...",
        type: "warning",
      });

      // Verificar si realmente se guardó
      try {
        const verificacion = await DnerhsApi.get(`/convenios/${params.convenioId}`);
        if (verificacion.data.estado === 'Convenio firmado') {
          Alert.show({
            message: "Los datos se guardaron correctamente",
            type: "success"
          });
          setTimeout(() => history.goBack(), 1500);
        } else {
          Alert.show({
            message: "No se pudo confirmar el guardado. Por favor, verifique manualmente.",
            type: "warning"
          });
        }
      } catch (verifyError) {
        Alert.show({
          message: "Error verificando el estado. Por favor, revise manualmente.",
          type: "warning"
        });
      }
    } else {
      // Otros errores
      Alert.show({
        message: "Error de conexión: " + (error.message || "Error desconocido"),
        type: "error"
      });
    }
  }
};


  return (
    <div>
      <Grid item xs={12}>
        <Typography align="center" variant="h5" color="primary">
          Registro de vigencia del convenio posterior a la firma
        </Typography>
      </Grid>
      <div>
        <FormProvider {...methods}>
          <form>
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="flex-start"
            >
              <Grid item xs={6} className={classes.form}>
                <Grid item xs={12} className={classes.field}>
                  <FormDatePicker
                    name="fechaFirma"
                    label="Fecha de firma del convenio"
                    required errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} className={classes.field}>
                  <FormDatePicker
                    name="fechaInicio"
                    label="Fecha de inicio de vigencia del convenio"
                    required errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} className={classes.field}>
                  <FormDatePicker
                    name="fechaFin"
                    label="Fecha de fin de vigencia del convenio"
                    required errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} className={classes.field}>
                  <FormInput
                    name="resolucion"
                    label="Número de Resolución"
                    required errorobj={errors}
                  />
                </Grid>
                <Grid item xs={12} className={classes.field}>
                  <FormCheckBox
                    checked={presentaCarta}
                    label="Presentó Carta de Compromiso?"
                    name="presentaCarta"
                    inputProps={{
                      "aria-label": "primary checkbox",
                    }}
                  />
                </Grid>
                {/* Nuevo checkbox de Renovación de Convenio */}
                <Grid item xs={12} className={classes.field}>
                  <FormCheckBox
                    checked={renovacionConvenio}
                    label="Renovación de Convenio?"
                    name="renovacionConvenio"
                    inputProps={{
                      "aria-label": "renovacion checkbox",
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </div>
      <Grid container direction="row" justifyContent="center" alignItems="center">
        <Grid item className={classes.field}>
          <Button
            variant="contained"
            color="default"
            component={Link}
            to={`/convenios/editar/${params.convenioId}`}
          >
            Atrás
          </Button>
        </Grid>
        <Grid item className={classes.field}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(onSubmit)}
          >
            Guardar
          </Button>
        </Grid>
      </Grid>
    </div>
  );
};

export default Form;